import { Injectable } from '@angular/core';
// Reused unchanged (research R-009) — the same hand-written EXIF/XMP parser the Electronic Field
// Map upload already ships, not a copy.
import { ExifReader } from '../../../upload-images/field-photo-upload-modal/utils/exif-reader.util';
import { GeoImageCard, isGeoTiffFile, isPhotoFile } from '../geo-image-upload-modal/geo-image-upload.models';
import { UploadedGeoImageService } from './uploaded-geo-image.service';

/** Applies a partial update to the checked card. The caller (an OnPush component) is responsible for
 * replacing its stored card with a new object built from the patch — mutating the card in place would
 * leave that object's `@Input` reference unchanged, so the OnPush child that renders it would never
 * pick the update up. */
export type GeoImageCardPatcher = (patch: Partial<GeoImageCard>) => void;

/**
 * Resolves a staged file's preview-stage status (FR-002/FR-003/FR-004/FR-005). JPEG/PNG is checked
 * entirely client-side, exactly as the Electronic Field Map upload already does. A GeoTIFF cannot be
 * parsed client-side (research R-009), so it is sent to the server's `Inspect` endpoint instead — the
 * server's answer is what ultimately decides accept/reject either way (research R-004: the client's
 * own read is never trusted for the gate itself, only used here for instant feedback).
 */
@Injectable({
  providedIn: 'root'
})
export class GeoImageFileValidator {
  constructor(private readonly uploadedGeoImageService: UploadedGeoImageService) {}

  checkCard(card: GeoImageCard, patch: GeoImageCardPatcher): void {
    patch({ isChecking: true });

    if (isPhotoFile(card.file)) {
      this.checkPhoto(card, patch);
      return;
    }

    if (isGeoTiffFile(card.file)) {
      this.checkRaster(card, patch);
      return;
    }

    patch({ isChecking: false, rejectionReason: 'UnsupportedFormat' });
  }

  private checkPhoto(card: GeoImageCard, patch: GeoImageCardPatcher): void {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      if (!arrayBuffer) {
        this.finishPhotoWithoutCoordinates(card, patch);
        return;
      }

      try {
        const view = new DataView(arrayBuffer);
        const coordinates = ExifReader.readExifGps(view);
        let captureDate: Date | null = null;
        try {
          captureDate = ExifReader.readExifDateTime(view);
        } catch {
          // A malformed date tag must not discard GPS coordinates already read above.
        }

        if (coordinates) {
          patch({
            captureDate: captureDate ?? new Date(card.file.lastModified),
            imageKind: 'Photo',
            coordinates,
            rejectionReason: null,
            isChecking: false
          });
        } else {
          // Not a rejection yet — the coordinate-entry modal can still supply a point (FR-004).
          this.finishPhotoWithoutCoordinates(card, patch, captureDate ?? new Date(card.file.lastModified));
        }
      } catch {
        this.finishPhotoWithoutCoordinates(card, patch);
      }
    };

    reader.onerror = () => this.finishPhotoWithoutCoordinates(card, patch);
    reader.readAsArrayBuffer(card.file);
  }

  private finishPhotoWithoutCoordinates(card: GeoImageCard, patch: GeoImageCardPatcher, captureDate?: Date): void {
    patch({
      imageKind: 'Photo',
      coordinates: null,
      captureDate: captureDate ?? new Date(card.file.lastModified),
      isChecking: false
    });
  }

  private checkRaster(card: GeoImageCard, patch: GeoImageCardPatcher): void {
    this.uploadedGeoImageService.inspect(card.file).subscribe({
      next: response => {
        if (response.accepted) {
          patch({
            isChecking: false,
            imageKind: 'Raster',
            footprintWkt: response.footprintWkt,
            captureDate: response.captureDateUtc ? new Date(response.captureDateUtc) : new Date(card.file.lastModified),
            rejectionReason: null
          });
        } else {
          // Keep imageKind='Raster' (this file already matched isGeoTiffFile) rather than nulling it:
          // rejectionReasonKey on the card needs it to pick MODAL_ERROR_RASTER_NO_GEOREFERENCE over the
          // Photo-specific message, and canBeSelected already excludes any card with a rejectionReason.
          patch({ isChecking: false, imageKind: 'Raster', rejectionReason: response.rejectionReason ?? 'NoGeoreferencing' });
        }
      },
      error: () => {
        patch({ isChecking: false, imageKind: 'Raster', rejectionReason: 'UnreadableFile' });
      }
    });
  }
}
